import type { SorterResult } from 'antd/es/table/interface';
import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AppAdminFormLabel, AppButton, AppModal, AppTextField } from '@/components/atoms';
import { Plus } from '@/components/atoms/AppIcon';
import { AdminUserForm, AdminUserListFilter, AdminUserListTable } from '@/components/organisms';
import AdminClinicsListTemplate from '@/components/templates/AdminClinicsListTemplate';
import { ADMIN_LIST_DEFAULT_PER_PAGE } from '@/constants';
import { DEFAULT_SITES } from '@/constants/routeSegments';
import {
  useAdminUserCreate,
  useAdminUsersCsv,
  useAdminUsersList,
  useAdminUserSsoLinkRetry,
  useAdminUserUpdate,
  useRootTenants,
} from '@/hooks/admin/useAdminUsers';
import { getAdminUser } from '@/services/admin/admins';
import type {
  AdminUserListItem,
  AdminUserMutationResult,
  AdminUsersListParams,
  AdminUserUpdatePayload,
} from '@/services/admin/admins/types';
import useAppToast from '@/hooks/useAppToast';
import { SSO_PASSWORD_MAX, SSO_SYSTEMS, type AdminUserFormData, type SsoSystem } from '@/schemas/adminUserSchema';
import { DEFAULT_CONFLICT_ERROR_MESSAGE, getApiErrorMessage } from '@/utils';
import type { UseFormSetError } from 'react-hook-form';

type SortKey = 'id' | 'name' | 'email' | 'last_login_at';
type SortOrder = 'ascend' | 'descend';

const VALID_SORT_KEYS: SortKey[] = ['name', 'email'];

/**
 * The API returns `string[]`; the form only accepts the two known values. Filtering rather than
 * casting: if the backend ever adds a system this build does not know about, drop it instead of
 * lying to the compiler and handing the checkboxes a value with no box to tick.
 */
const toSsoSystems = (systems?: string[]): SsoSystem[] =>
  (systems ?? []).filter((system): system is SsoSystem =>
    (SSO_SYSTEMS as readonly string[]).includes(system)
  );

/** Chỉ những key này mới có ô trên form để gắn thông điệp lỗi vào. */
const FORM_FIELD_KEYS = [
  'name',
  'email',
  'password',
  'manage_site',
  'sso_systems',
  'sso_login_id',
] as const;

const isFormFieldKey = (key: string): key is (typeof FORM_FIELD_KEYS)[number] =>
  (FORM_FIELD_KEYS as readonly string[]).includes(key);

/**
 * Đổ toàn bộ lỗi 422 của server vào đúng ô của nó.
 *
 * VÌ SAO PHẢI DUYỆT HẾT. Bản cũ chỉ đọc `errors.email` rồi `errors.sso_login_id`, mỗi nhánh đều
 * `return` — nghĩa là mọi lỗi khác (`name`, `password`, `manage_site`, `sso_systems`) bị nuốt
 * trọn: modal đứng im, không thông báo, không ô nào đỏ, người dùng bấm 更新 lần nữa và lại im.
 * Trường hợp có thật: mật khẩu 9 ký tự khi đã tick hệ thống — zod cũ cho qua (trần 72), server
 * chặn ở 8.
 *
 * VÌ SAO KHÔNG CÒN ĐÈ RIÊNG THÔNG ĐIỆP CHO `email`. Trước đây frontend tự viết đè
 * 「メールアドレスは既に存在します。」 cho MỌI lỗi email. Nhưng backend đã có sẵn đúng câu đó ở
 * `email.unique` (AdminUserStoreRequest::messages), nên viết đè chỉ tạo ra khả năng nói sai: một
 * email quá 100 ký tự sẽ bị báo là "đã tồn tại". Dùng thẳng thông điệp của server.
 *
 * Lỗi đã có ô riêng thì chỉ hiện dưới ô, không bắn thêm toast 「エラーが発生しました。」 — giống
 * hệt nhánh lỗi phía client của AdminUserForm, để hai đường thất bại trông như nhau.
 */
const applyServerErrors = (
  err: any,
  setError: UseFormSetError<AdminUserFormData>,
  toast: ReturnType<typeof useAppToast>
): void => {
  const apiErrors = err?.response?.data?.errors;

  if (!apiErrors || typeof apiErrors !== 'object') {
    // Không có phần `errors`: 500, 502, mất mạng... Chỉ còn một câu để nói.
    toast.error(getApiErrorMessage(err, 'エラーが発生しました。'));
    return;
  }

  // Lỗi của những key KHÔNG có ô nào trên form. Không có chỗ để hiện, nên phải bắn ra toast —
  // nếu không thì lại rơi vào đúng cái bẫy "im lặng" vừa sửa ở trên.
  const orphanMessages: string[] = [];

  Object.entries(apiErrors as Record<string, unknown>).forEach(([key, value]) => {
    const message = Array.isArray(value) ? String(value[0] ?? '') : String(value ?? '');
    if (!message) return;

    if (isFormFieldKey(key)) {
      setError(key, { type: 'server', message });
      return;
    }

    orphanMessages.push(message);
  });

  orphanMessages.forEach(message => toast.error(message));
};

const AdminUserListPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  const isSuperAdminRoute = site === DEFAULT_SITES.SUPER_ADMIN;

  const { data: rootTenants = [] } = useRootTenants(site ?? null, isSuperAdminRoute);

  const searchName = searchParams.get('search_name') ?? '';
  const searchEmail = searchParams.get('search_email') ?? '';
  const searchManageSites = searchParams.getAll('manage_sites').filter(Boolean);
  const sortByParam = searchParams.get('sort_by');
  const orderParam = searchParams.get('order') as 'asc' | 'desc' | null;
  const page = Number(searchParams.get('page')) || 1;

  const sortKey = VALID_SORT_KEYS.includes(sortByParam as SortKey)
    ? (sortByParam as SortKey)
    : null;
  const sortOrder: SortOrder | null =
    orderParam === 'asc' ? 'ascend' : orderParam === 'desc' ? 'descend' : null;

  const params: AdminUsersListParams = useMemo(
    () => ({
      search_name: searchName.trim() || undefined,
      search_email: searchEmail.trim() || undefined,
      search_manage_sites: searchManageSites.length > 0 ? searchManageSites : undefined,
      sort: sortKey && orderParam ? [sortKey, orderParam] : undefined,
      page,
      per_page: ADMIN_LIST_DEFAULT_PER_PAGE,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams]
  );

  const { data, isLoading } = useAdminUsersList(site ?? null, params);
  const rows: AdminUserListItem[] = data?.data?.data ?? [];
  const meta = data?.meta ?? {
    current_page: 1,
    last_page: 1,
    per_page: ADMIN_LIST_DEFAULT_PER_PAGE,
    total: 0,
  };

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [ssoPasswordNotice, setSsoPasswordNotice] = useState<AdminUserMutationResult | null>(null);
  const [editingUser, setEditingUser] = useState<AdminUserListItem | null>(null);
  const createMutation = useAdminUserCreate(site ?? null);
  const updateMutation = useAdminUserUpdate(site ?? null);
  const ssoLinkRetryMutation = useAdminUserSsoLinkRetry(site ?? null);

  // Bản ghi đang chờ người dùng nhập lại mật khẩu để liên kết. `null` = chưa mở ô nhập.
  const [ssoRetryTarget, setSsoRetryTarget] = useState<AdminUserListItem | null>(null);
  const [ssoRetryPassword, setSsoRetryPassword] = useState('');
  const [ssoRetryPasswordError, setSsoRetryPasswordError] = useState<string | null>(null);

  // Lấy từ chính mutation thay vì nuôi thêm một state song song: chỉ có một yêu cầu chạy mỗi lúc,
  // và một state riêng sẽ có lúc lệch với thực tế nếu quên xoá ở một nhánh lỗi nào đó.
  const retryingSsoLinkId = ssoLinkRetryMutation.isPending
    ? (ssoLinkRetryMutation.variables?.id ?? null)
    : null;

  const manageSiteOptions = useMemo(() => {
    if (isSuperAdminRoute) {
      return [
        { label: 'スーパー管理者', value: DEFAULT_SITES.SUPER_ADMIN },
        ...rootTenants.map(t => ({ label: t.name, value: t.site_type })),
      ];
    }
    return site ? [{ label: site, value: site }] : [];
  }, [isSuperAdminRoute, rootTenants, site]);

  const handleCreate = useCallback(
    (formData: AdminUserFormData, setError: UseFormSetError<AdminUserFormData>) => {
      createMutation.mutate(
        { ...formData, password: formData.password as string },
        {
          onSuccess: result => {
            setIsCreateModalOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['admin', site, 'admins'] });
            toast.success('更新しました。');
            // This is the only moment the plaintext exists on the client. See the notice modal.
            if (result.ssoPassword) setSsoPasswordNotice(result);
          },
          onError: (err: any) => applyServerErrors(err, setError, toast),
        }
      );
    },
    [createMutation, queryClient, site, toast]
  );

  const handleEdit = useCallback(
    async (record: AdminUserListItem) => {
      try {
        // Seed the modal from the freshly fetched record, not from the list row: the list can be
        // stale, and the SSO fields are re-submitted on save, so stale values overwrite whatever
        // somebody else just changed.
        setEditingUser(await getAdminUser(site!, record.id));
      } catch {
        toast.error(DEFAULT_CONFLICT_ERROR_MESSAGE);
      }
    },
    [site, toast]
  );

  const handleUpdate = useCallback(
    (formData: AdminUserFormData, setError: UseFormSetError<AdminUserFormData>) => {
      if (!editingUser) return;
      const payload: AdminUserUpdatePayload = {
        name: formData.name,
        email: formData.email,
        manage_site: formData.manage_site,
        // Always send both: unticking everything is a deliberate choice and has to reach the
        // backend, not turn into an absent key that reads as "leave it alone".
        sso_systems: formData.sso_systems ?? [],
        sso_login_id: formData.sso_login_id ?? '',
      };
      if (formData.password) {
        payload.password = formData.password;
      }
      updateMutation.mutate(
        { id: editingUser.id, payload },
        {
          onSuccess: result => {
            setEditingUser(null);
            void queryClient.invalidateQueries({ queryKey: ['admin', site, 'admins'] });
            toast.success('更新しました。');
            if (result.ssoPassword) setSsoPasswordNotice(result);
          },
          onError: (err: any) => {
            // 404 = bản ghi đã bị người khác xoá giữa chừng. Không phải lỗi của form, và không có
            // ô nào để gắn vào.
            if (err?.response?.status === 404) {
              toast.error(DEFAULT_CONFLICT_ERROR_MESSAGE);
              return;
            }
            applyServerErrors(err, setError, toast);
          },
        }
      );
    },
    [editingUser, updateMutation, queryClient, site, toast]
  );

  /**
   * Nút 「SSO連携」 ở màn list (BD sheet 2, mục 7.0).
   *
   * Lần bấm đầu KHÔNG gửi mật khẩu: phần lớn trường hợp tài khoản Cognito đã tạo được, chỉ mỗi
   * `sso_identity_links` là hỏng, và lúc đó server chỉ cần `subject` để ghi nốt. Chỉ khi server
   * trả 422 kèm `errors.password` — tức tài khoản Cognito cũng chưa từng tạo được — mới phải hỏi
   * mật khẩu, vì bản rõ đã mất hẳn (`admins.password` là bcrypt, `admin_sso_grants` cố tình không
   * giữ mật khẩu).
   */
  const runSsoLinkRetry = useCallback(
    (record: AdminUserListItem, password?: string) => {
      ssoLinkRetryMutation.mutate(
        { id: record.id, password },
        {
          onSuccess: () => {
            setSsoRetryTarget(null);
            setSsoRetryPassword('');
            setSsoRetryPasswordError(null);
            void queryClient.invalidateQueries({ queryKey: ['admin', site, 'admins'] });
            toast.success('SSO連携しました。');
          },
          onError: (err: any) => {
            const passwordError: string | undefined = err?.response?.data?.errors?.password?.[0];

            if (passwordError) {
              setSsoRetryTarget(record);
              // Lần mở đầu tiên chưa phải là "sai" — người dùng chưa gõ gì cả. Chỉ hiện thông điệp
              // của server khi họ ĐÃ gửi một mật khẩu và mật khẩu đó bị từ chối.
              setSsoRetryPasswordError(password ? passwordError : null);
              return;
            }

            setSsoRetryPasswordError(null);
            toast.error(
              getApiErrorMessage(err, 'SSO連携に失敗しました。時間をおいて再度お試しください。')
            );
          },
        }
      );
    },
    [ssoLinkRetryMutation, queryClient, site, toast]
  );

  const handleRetrySsoLink = useCallback(
    (record: AdminUserListItem) => runSsoLinkRetry(record),
    [runSsoLinkRetry]
  );

  const closeSsoRetryModal = useCallback(() => {
    setSsoRetryTarget(null);
    setSsoRetryPassword('');
    setSsoRetryPasswordError(null);
  }, []);

  const handleSearch = useCallback(
    (filters: { name: string; email: string; manageSites: string[] }) => {
      const nextParams = new URLSearchParams();
      if (filters.name.trim()) nextParams.set('search_name', filters.name.trim());
      if (filters.email.trim()) nextParams.set('search_email', filters.email.trim());
      if (isSuperAdminRoute) {
        filters.manageSites.forEach(v => nextParams.append('manage_sites', v));
      }
      nextParams.set('page', '1');
      setSearchParams(nextParams);
    },
    [isSuperAdminRoute, setSearchParams]
  );

  const handleTableChange = useCallback(
    (
      _p: any,
      _f: any,
      sorter: SorterResult<AdminUserListItem> | SorterResult<AdminUserListItem>[]
    ) => {
      const s = Array.isArray(sorter) ? sorter[0] : sorter;
      const nextParams = new URLSearchParams(searchParams);
      if (s.order) {
        nextParams.set('sort_by', s.columnKey as string);
        nextParams.set('order', s.order === 'ascend' ? 'asc' : 'desc');
      } else {
        nextParams.delete('sort_by');
        nextParams.delete('order');
      }
      nextParams.set('page', '1');
      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams]
  );

  const { mutate: downloadCsv, isPending: csvLoading } = useAdminUsersCsv();
  const handleCsv = useCallback(() => downloadCsv(params), [downloadCsv, params]);

  const handlePageChange = useCallback(
    (p: number) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('page', String(p));
      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams]
  );

  return (
    <>
      <AdminClinicsListTemplate
        breadcrumbItems={[{ title: '管理者' }]}
        title="管理者"
        filter={
          <AdminUserListFilter
            isSuperAdminRoute={isSuperAdminRoute}
            rootTenants={rootTenants}
            defaultSearchName={searchName}
            defaultSearchEmail={searchEmail}
            defaultSearchManageSites={searchManageSites}
            onSearch={handleSearch}
            onCsv={handleCsv}
            csvLoading={csvLoading}
          />
        }
        betweenFilterAndTable={
          <div className="mb-4">
            <AppButton icon={<Plus />} type="primary" onClick={() => setIsCreateModalOpen(true)}>
              新規
            </AppButton>
          </div>
        }
        table={
          <AdminUserListTable
            isSuperAdminRoute={isSuperAdminRoute}
            rows={rows}
            isLoading={isLoading}
            meta={meta}
            sortKey={sortKey}
            sortOrder={sortOrder}
            onChange={handleTableChange}
            onPageChange={handlePageChange}
            onEdit={handleEdit}
            onRetrySsoLink={handleRetrySsoLink}
            retryingSsoLinkId={retryingSsoLinkId}
          />
        }
      />

      <AppModal
        isOpen={isCreateModalOpen}
        title="管理者作成"
        onClose={() => setIsCreateModalOpen(false)}
        footer={null}
      >
        <AdminUserForm
          manageSiteOptions={manageSiteOptions}
          isSuperAdminRoute={isSuperAdminRoute}
          defaultManageSite={site}
          isSubmitting={createMutation.isPending}
          onSubmit={handleCreate}
        />
      </AppModal>

      <AppModal
        isOpen={!!editingUser}
        title={editingUser ? `管理者編集：${editingUser.id}` : '管理者編集'}
        onClose={() => setEditingUser(null)}
        footer={null}
      >
        {editingUser && (
          <AdminUserForm
            isEdit
            manageSiteOptions={manageSiteOptions}
            isSuperAdminRoute={isSuperAdminRoute}
            isSubmitting={updateMutation.isPending}
            initialValues={{
              name: editingUser.name,
              email: editingUser.email,
              manage_site: editingUser.manage_site,
              sso_systems: toSsoSystems(editingUser.sso_systems),
              sso_password_optional_systems: editingUser.sso_password_optional_systems,
              sso_login_id: editingUser.sso_login_id ?? '',
            }}
            onSubmit={handleUpdate}
          />
        )}
      </AppModal>

      <AppModal
        isOpen={!!ssoRetryTarget}
        title="SSO連携"
        maxWidth="max-w-lg"
        onClose={closeSsoRetryModal}
        footer={null}
      >
        {ssoRetryTarget && (
          <div className="space-y-4" data-testid="sso-retry-password-modal">
            {/*
              Câu này phải nói ĐỦ ba hệ. Bản trước chỉ nhắc 審査・開示, trong khi mật khẩu nhập ở
              đây nay được đặt cho cả chính cổng quản trị này (`admins.password`) — một nút tên
              「SSO連携」 mà âm thầm đổi mật khẩu đăng nhập là đúng thứ không được phép làm.
            */}
            <p className="text-sm">
              {ssoRetryTarget.name}
              のアカウント連携を完了するため、パスワードを入力してください。
            </p>
            <p className="text-sm">
              入力したパスワードは、この管理者ポータル・審査システム・開示システムのすべてに設定されます。
            </p>
            <div>
              <AppAdminFormLabel label="パスワード" required />
              <AppTextField
                type="password"
                className="w-full"
                maxLength={SSO_PASSWORD_MAX}
                autoComplete="new-password"
                value={ssoRetryPassword}
                onChange={event => {
                  setSsoRetryPassword(event.target.value);
                  setSsoRetryPasswordError(null);
                }}
                data-testid="sso-retry-password-input"
                status={ssoRetryPasswordError ? 'error' : undefined}
              />
              {ssoRetryPasswordError && (
                <p className="text-red-500 text-sm mt-1" data-testid="sso-retry-password-error">
                  {ssoRetryPasswordError}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {SSO_PASSWORD_MAX}文字以内で入力してください。連携先システムにも設定されます。
              </p>
              <p className="text-xs text-gray-500 mt-1">
                入力しない場合、パスワードは変更されません。
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <AppButton onClick={closeSsoRetryModal}>キャンセル</AppButton>
              <AppButton
                type="primary"
                loading={ssoLinkRetryMutation.isPending}
                disabled={ssoRetryPassword.trim() === ''}
                onClick={() => runSsoLinkRetry(ssoRetryTarget, ssoRetryPassword)}
                data-testid="sso-retry-password-submit"
              >
                連携する
              </AppButton>
            </div>
          </div>
        )}
      </AppModal>

      <AppModal
        isOpen={!!ssoPasswordNotice}
        title="SSO連携アカウントのパスワード"
        maxWidth="max-w-lg"
        onClose={() => setSsoPasswordNotice(null)}
        footer={null}
      >
        {ssoPasswordNotice?.ssoPassword && (
          <div className="space-y-4">
            <p className="text-sm">
              審査システム・開示システムのアカウントには、下記のパスワードがそのまま登録されました。
              <strong>この画面を閉じると二度と表示されません。</strong>
              必ず控えてから閉じてください。
            </p>
            <dl className="rounded border border-gray-200 bg-gray-50 p-4 text-sm">
              <div className="flex gap-3">
                <dt className="w-28 shrink-0 text-gray-600">ログインID</dt>
                <dd className="font-mono">{ssoPasswordNotice.admin.sso_login_id ?? '-'}</dd>
              </div>
              <div className="mt-2 flex gap-3">
                <dt className="w-28 shrink-0 text-gray-600">パスワード</dt>
                <dd className="font-mono select-all break-all">{ssoPasswordNotice.ssoPassword}</dd>
              </div>
            </dl>
            <div className="flex justify-end">
              <AppButton type="primary" onClick={() => setSsoPasswordNotice(null)}>
                控えました
              </AppButton>
            </div>
          </div>
        )}
      </AppModal>
    </>
  );
};

export default AdminUserListPage;
